import React, { useState, useEffect } from 'react';
import './App.css';
import LoginScreen from './components/LoginScreen';
import { hasValidSession, endSession } from './auth';
import TopicsTab from './components/TopicsTab';
import QuestionsTab from './components/QuestionsTab';
import UsersTab from './components/UsersTab';
import FeedbackTab from './components/FeedbackTab';
import CertificationsTab from './components/CertificationsTab';
import SubscriptionsTab from './components/SubscriptionsTab';
import RecognitionTab from './components/RecognitionTab';
import MockInterviewsTab from './components/MockInterviewsTab';
import MentorsTab from './components/MentorsTab';
import TestTasksTab from './components/TestTasksTab';
import HRAssistantTab from './components/HRAssistantTab';
import ResumeBuilderTab from './components/ResumeBuilderTab';

const TABS = [
  { id: 'topics', label: 'Темы' },
  { id: 'questions', label: 'Вопросы' },
  { id: 'users', label: 'Пользователи' },
  { id: 'feedback', label: 'Обратная связь' },
  { id: 'certifications', label: 'Сертификации' },
  { id: 'subscriptions', label: 'Подписки' },
  { id: 'recognition', label: 'Обработка собеседований' },
  { id: 'mock-interviews', label: 'Mock-собеседования' },
  { id: 'mentors', label: 'Менторы' },
  { id: 'test-tasks', label: 'Тестовые задания' },
  { id: 'hr-assistant', label: 'HRAssistant' },
  { id: 'resume-builder', label: 'Конструктор резюме' },
];

function App() {
  const [activeTab, setActiveTab] = useState('topics');
  const [menuOpen, setMenuOpen] = useState(false);
  const [authorized, setAuthorized] = useState(() => hasValidSession());

  // Сессия может истечь во время работы — проверяем периодически и при
  // возвращении на вкладку, чтобы панель не осталась открытой после протухания.
  useEffect(() => {
    if (!authorized) return undefined;
    const check = () => {
      if (!hasValidSession()) setAuthorized(false);
    };
    const id = setInterval(check, 60 * 1000);
    document.addEventListener('visibilitychange', check);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', check);
    };
  }, [authorized]);

  const handleTabSelect = (id) => {
    setActiveTab(id);
    setMenuOpen(false);
  };

  const handleLogout = () => {
    endSession();
    setAuthorized(false);
    setMenuOpen(false);
  };

  if (!authorized) {
    return <LoginScreen onSuccess={() => setAuthorized(true)} />;
  }

  return (
    <div className="App">
      <header className="app-header">
        <button
          type="button"
          className="burger-button"
          onClick={() => setMenuOpen(true)}
          aria-label="Открыть меню"
        >
          <span className="burger-icon" aria-hidden>☰</span>
        </button>
        <div className="tabs">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              className={`tab-button ${activeTab === id ? 'active' : ''}`}
              onClick={() => setActiveTab(id)}
            >
              {label}
            </button>
          ))}
        </div>
        <button type="button" className="logout-button" onClick={handleLogout}>
          Выйти
        </button>
      </header>

      <div
        className={`nav-overlay ${menuOpen ? 'nav-overlay-open' : ''}`}
        onClick={() => setMenuOpen(false)}
        onKeyDown={(e) => e.key === 'Escape' && setMenuOpen(false)}
        role="button"
        tabIndex={-1}
        aria-label="Закрыть меню"
        aria-hidden={!menuOpen}
      />
      <div className={`nav-panel ${menuOpen ? 'nav-panel-open' : ''}`}>
        <div className="nav-panel-header">
          <span className="nav-panel-title">Меню</span>
          <button
            type="button"
            className="nav-panel-close"
            onClick={() => setMenuOpen(false)}
            aria-label="Закрыть"
          >
            ✕
          </button>
        </div>
        <nav className="nav-panel-list">
          {TABS.map(({ id, label }) => (
            <button
              key={id}
              type="button"
              className={`nav-panel-item ${activeTab === id ? 'active' : ''}`}
              onClick={() => handleTabSelect(id)}
            >
              {label}
            </button>
          ))}
        </nav>
      </div>

      <div className="tab-content">
        {activeTab === 'topics' && <TopicsTab />}
        {activeTab === 'questions' && <QuestionsTab />}
        {activeTab === 'users' && <UsersTab />}
        {activeTab === 'feedback' && <FeedbackTab />}
        {activeTab === 'certifications' && <CertificationsTab />}
        {activeTab === 'subscriptions' && <SubscriptionsTab />}
        {activeTab === 'recognition' && <RecognitionTab />}
        {activeTab === 'mock-interviews' && <MockInterviewsTab />}
        {activeTab === 'mentors' && <MentorsTab />}
        {activeTab === 'test-tasks' && <TestTasksTab />}
        {activeTab === 'hr-assistant' && <HRAssistantTab />}
        {activeTab === 'resume-builder' && <ResumeBuilderTab />}
      </div>
    </div>
  );
}

export default App;
